import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDate, formatDateTime, formatEuro } from '@rental/i18n'
import { Button, Checkbox, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api } from '../api'
import { canDo } from '../booking-can'
import { ChoiceChips } from '../components/ChoiceChips'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { ReasonList } from '../components/SubPanel'
import {
  Facts,
  FIELD,
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  ProblemNotice,
  SHORT_FIELD,
  StaffInput,
  StaffTextarea,
} from '../components/ui'
import { type Chosen, CustomerPicker } from '../customer/CustomerPicker'
import { reasonText } from '../eligibility-words'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import { e164 } from '../phone'
import { refreshAfterChange } from '../refresh'
import {
  type DriverDraft,
  EMPTY_DRIVER,
  EMPTY_LICENCE,
  type LicenceDraft,
  secondDriverBody,
  secondDriverReasons,
} from './second-driver-form'

type Detail = ApiSchemas['StaffBookingDetail']
type Driver = NonNullable<Detail['secondDriver']>
type Reason = Detail['eligibilityReasons'][number]

/**
 * Q-162 (RES-3): the booking's second driver, entered at the counter before
 * the hand-over. Added under terms that allow one, at the fee the
 * configuration sets (once per rental, into the total); an ineligible one only
 * with the Owner's reason. Changing or taking them off voids a contract or a
 * CaRGOS report made before: the booking's version moves. The section says
 * why nobody can be added when that is so; a booking past its hand-over with
 * none shows nothing.
 */
export function SecondDriverSection({ detail }: { detail: Detail }) {
  const { locale, messages: m } = useLocale()
  const copy = m.secondDriver
  const may = canDo(detail.can)
  const driver = detail.secondDriver
  const availability = detail.secondDriverAvailability
  const beforeHandover = availability.available || availability.reason !== 'not_before_handover'
  const office = may('secondDriver')
  const [editing, setEditing] = useState(false)
  const [outcome, setOutcome] = useState<{ key: number; text: string } | null>(null)
  const removeDialog = useRef<HTMLDialogElement>(null)
  const actions = useRef<HTMLDivElement>(null)
  const { send, busy, problem, clear } = useSend()
  const queryClient = useQueryClient()

  if (!driver && (!office || !beforeHandover)) return null

  const said = (text: string) => setOutcome((current) => ({ key: (current?.key ?? 0) + 1, text }))

  async function remove() {
    const changed = await send(() =>
      call(
        api.DELETE('/v1/staff/bookings/{code}/second-driver', {
          params: {
            path: { code: detail.booking.code },
            query: { expectedVersion: detail.version },
          },
        }),
      ),
    )
    if (!changed) return
    removeDialog.current?.close()
    said(format(copy.removed, { total: formatEuro(changed.totalCents, locale) }))
    await refreshAfterChange(queryClient, detail.booking.code)
  }

  return (
    <Section title={copy.title}>
      {outcome ? (
        <OutcomeNotice key={outcome.key}>
          <p>{outcome.text}</p>
        </OutcomeNotice>
      ) : null}
      {driver ? <DriverFacts driver={driver} /> : <p>{copy.none}</p>}
      {office && beforeHandover && !availability.available ? (
        <p className="text-sm">
          {availability.reason ? copy.unavailable[availability.reason] : null}
        </p>
      ) : null}
      {office && beforeHandover && !editing ? (
        <div ref={actions} className="flex flex-wrap gap-3">
          {availability.available ? (
            <Button
              variant={driver ? 'outline' : 'staff'}
              className={driver ? 'rounded-sm' : undefined}
              onClick={() => {
                setOutcome(null)
                clear()
                setEditing(true)
              }}
            >
              {driver ? copy.replace : copy.add}
            </Button>
          ) : null}
          {driver ? (
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => {
                clear()
                removeDialog.current?.showModal()
              }}
            >
              {copy.remove}
            </Button>
          ) : null}
        </div>
      ) : null}
      {editing && availability.available ? (
        <SecondDriverForm
          detail={detail}
          feeCents={availability.feeCents ?? 0}
          onDone={(text) => {
            flushSync(() => setEditing(false))
            if (text) said(text)
            else actions.current?.querySelector<HTMLElement>('button')?.focus()
          }}
        />
      ) : null}
      {driver ? (
        <ConfirmDialog
          dialogRef={removeDialog}
          title={copy.removeTitle}
          confirmLabel={copy.removeConfirm}
          cancelLabel={copy.cancel}
          busy={busy}
          onConfirm={() => void remove()}
        >
          <p>
            {format(copy.removeBody, {
              name: `${driver.firstName} ${driver.lastName}`,
              amount: formatEuro(driver.feeCents, locale),
            })}
          </p>
          <ProblemNotice problem={problem} />
        </ConfirmDialog>
      ) : null}
    </Section>
  )
}

/** The second driver on file: who, their licence, the fee, the verdict, the identity. */
function DriverFacts({ driver }: { driver: Driver }) {
  const { locale, messages: m } = useLocale()
  const copy = m.secondDriver
  const licence = driver.licence
  const items: (readonly [string, ReactNode])[] = [
    [m.booking.name, `${driver.firstName} ${driver.lastName}`],
    ...(driver.birthDate
      ? ([[m.booking.birthDate, formatDate(locale, driver.birthDate, 'short')]] as const)
      : []),
    ...(driver.phone ? ([[m.booking.phone, driver.phone]] as const) : []),
    [
      copy.licence,
      licence
        ? [
            licence.number,
            licence.issuingCountry,
            licence.categories.join(', '),
            licence.expiresOn
              ? format(copy.expires, { date: formatDate(locale, licence.expiresOn, 'short') })
              : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : copy.noLicence,
    ],
    [
      copy.fee,
      <span key="fee" className="figures">
        {formatEuro(driver.feeCents, locale)}
      </span>,
    ],
    [copy.identity, driver.identityRecorded ? copy.identityRecorded : copy.identityMissing],
  ]
  const verdict = driver.eligibility
  return (
    <div className="flex flex-col gap-3">
      <Facts items={items} />
      <div>
        <StatusChip
          tone={
            verdict.status === 'eligible'
              ? 'success'
              : verdict.status === 'ineligible'
                ? 'danger'
                : 'warning'
          }
        >
          {m.eligibility[verdict.status]}
        </StatusChip>
      </div>
      {verdict.reasons.length > 0 ? (
        <ul className="flex list-disc flex-col gap-1 ps-6">
          {verdict.reasons.map((reason) => (
            <li key={reason.code}>{reasonText(reason as Reason, locale, m)}</li>
          ))}
        </ul>
      ) : null}
      {driver.overrideReason ? (
        <Notice tone="warning">
          <p>{format(copy.overridden, { reason: driver.overrideReason })}</p>
        </Notice>
      ) : null}
      <p className="text-sm text-text-muted">
        {format(copy.addedBy, {
          name: driver.addedBy,
          date: formatDateTime(locale, driver.addedAt),
        })}
      </p>
    </div>
  )
}

/**
 * Adding or replacing: someone on file or a new person, with a licence; the
 * fee and the new total said before saving. An ineligible driver comes back
 * from the server with the rules' reasons: the Owner may then give a reason,
 * everyone else is told only the Owner can.
 */
function SecondDriverForm({
  detail,
  feeCents,
  onDone,
}: {
  detail: Detail
  feeCents: number
  onDone: (outcome: string | null) => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.secondDriver
  const { access } = useAccess()
  const owner = can(access, 'checkout.override')
  const queryClient = useQueryClient()
  const { send, busy, problem, clear } = useSend()
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [chosen, setChosen] = useState<Chosen | null>(null)
  const [person, setPerson] = useState<DriverDraft>(EMPTY_DRIVER)
  const [licence, setLicence] = useState<LicenceDraft>(EMPTY_LICENCE)
  // Set by the server's answer: the rules say no, with their reasons.
  const [ineligible, setIneligible] = useState<Reason[] | null>(null)
  const [overrideReason, setOverrideReason] = useState('')
  const reasonsId = useId()
  const form = useRef<HTMLDivElement>(null)

  const current = detail.secondDriver
  const total = detail.booking.totalCents - (current?.feeCents ?? 0) + feeCents
  const overriding = ineligible !== null && owner
  const state = { mode, chosen: chosen?.id ?? null, person, licence, overriding, overrideReason }
  const reasons = secondDriverReasons(state).map((reason) => copy.why[reason])
  const blockedForAgent = ineligible !== null && !owner
  const ready = reasons.length === 0 && !blockedForAgent && !busy

  const setPersonField = <K extends keyof DriverDraft>(key: K, value: DriverDraft[K]) => {
    setPerson((draft) => ({ ...draft, [key]: value }))
    setIneligible(null)
  }
  const setLicenceField = <K extends keyof LicenceDraft>(key: K, value: LicenceDraft[K]) => {
    setLicence((draft) => ({ ...draft, [key]: value }))
    setIneligible(null)
  }

  async function save() {
    if (!ready) return
    const body = secondDriverBody({ ...state, expectedVersion: detail.version, phone: e164 })
    if (!body) return
    // The rules' no is an answer to act on here, not a problem to show alone.
    let refusedByRules = false
    const result = await send(async () => {
      const answer = await call(
        api.PUT('/v1/staff/bookings/{code}/second-driver', {
          params: { path: { code: detail.booking.code } },
          body,
        }),
      )
      if (
        !answer.ok &&
        answer.problem.kind === 'api' &&
        answer.problem.code === 'second_driver_ineligible'
      ) {
        const found = answer.problem.details.reasons
        setIneligible(Array.isArray(found) ? (found as Reason[]) : [])
        refusedByRules = true
      }
      return answer
    })
    if (refusedByRules) clear()
    if (!result) return
    const unchanged = result.version === detail.version
    await refreshAfterChange(queryClient, detail.booking.code)
    onDone(unchanged ? copy.unchanged : current ? copy.replaced : copy.added)
  }

  return (
    <div ref={form} className="flex flex-col gap-4 rounded-sm bg-surface-well p-3">
      <ChoiceChips
        name={`second-driver-who-${detail.booking.code}`}
        label={copy.who}
        options={(['existing', 'new'] as const).map((value) => ({
          value,
          label: copy.whoOptions[value],
        }))}
        value={mode}
        onChange={(value) => {
          setMode(value)
          setIneligible(null)
        }}
      />
      {mode === 'existing' ? (
        <CustomerPicker
          chosen={chosen}
          onChoose={(customer) => {
            setChosen(customer)
            setIneligible(null)
          }}
        />
      ) : (
        <>
          <div className={FIELD_ROW}>
            <Field className={FIELD} label={m.newBooking.firstName} required>
              {(control) => (
                <StaffInput
                  {...control}
                  autoComplete="off"
                  maxLength={80}
                  value={person.firstName}
                  onChange={(event) => setPersonField('firstName', event.target.value)}
                />
              )}
            </Field>
            <Field className={FIELD} label={m.newBooking.lastName} required>
              {(control) => (
                <StaffInput
                  {...control}
                  autoComplete="off"
                  maxLength={80}
                  value={person.lastName}
                  onChange={(event) => setPersonField('lastName', event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className={FIELD_ROW}>
            <FieldPair>
              <Field
                className={PAIRED_FIELD}
                label={m.newBooking.birthDate}
                optionalLabel={m.newBooking.optional}
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    type="date"
                    value={person.birthDate}
                    onChange={(event) => setPersonField('birthDate', event.target.value)}
                  />
                )}
              </Field>
              <Field
                className={PAIRED_FIELD}
                label={m.newBooking.nationality}
                optionalLabel={m.newBooking.optional}
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    maxLength={2}
                    autoCapitalize="characters"
                    value={person.nationality}
                    onChange={(event) =>
                      setPersonField('nationality', event.target.value.toUpperCase())
                    }
                  />
                )}
              </Field>
            </FieldPair>
            <Field
              className={FIELD}
              label={m.newBooking.phone}
              optionalLabel={m.newBooking.optional}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  type="tel"
                  autoComplete="off"
                  maxLength={20}
                  value={person.phone}
                  onChange={(event) => setPersonField('phone', event.target.value)}
                />
              )}
            </Field>
          </div>
        </>
      )}

      <fieldset className="flex min-w-0 flex-col gap-4">
        <legend className="mb-2 text-lg font-bold text-brand-strong">{copy.licence}</legend>
        <p className="text-sm text-text-muted">
          {mode === 'existing' ? copy.licenceHintExisting : copy.licenceHint}
        </p>
        <div className={FIELD_ROW}>
          <Field className={FIELD} label={m.newBooking.licenceNumber}>
            {(control) => (
              <StaffInput
                {...control}
                autoComplete="off"
                value={licence.number}
                onChange={(event) => setLicenceField('number', event.target.value)}
              />
            )}
          </Field>
          <Field className={SHORT_FIELD} label={m.newBooking.licenceCountry}>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={2}
                value={licence.country}
                onChange={(event) => setLicenceField('country', event.target.value.toUpperCase())}
              />
            )}
          </Field>
        </div>
        <div className={FIELD_ROW}>
          <Field className={SHORT_FIELD} label={m.newBooking.licenceCategory}>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={10}
                value={licence.category}
                onChange={(event) => setLicenceField('category', event.target.value.toUpperCase())}
              />
            )}
          </Field>
          <FieldPair>
            <Field className={PAIRED_FIELD} label={m.newBooking.licenceFirstIssued}>
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={licence.firstIssued}
                  onChange={(event) => setLicenceField('firstIssued', event.target.value)}
                />
              )}
            </Field>
            <Field className={PAIRED_FIELD} label={m.newBooking.licenceExpires}>
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={licence.expires}
                  onChange={(event) => setLicenceField('expires', event.target.value)}
                />
              )}
            </Field>
          </FieldPair>
        </div>
        <Checkbox
          label={m.newBooking.hasIdp}
          checked={licence.hasIdp}
          onChange={(event) => setLicenceField('hasIdp', event.target.checked)}
        />
      </fieldset>

      <p className="font-medium">
        {feeCents === 0
          ? format(copy.feeFree, { total: formatEuro(total, locale) })
          : format(copy.feeLine, {
              amount: formatEuro(feeCents, locale),
              total: formatEuro(total, locale),
            })}
      </p>
      <p className="text-sm text-text-muted">{copy.voids}</p>

      {ineligible ? (
        <Notice tone={owner ? 'warning' : 'danger'} live="assertive">
          <p className="font-medium">{copy.ineligible}</p>
          {ineligible.length > 0 ? (
            <ul className="flex list-disc flex-col gap-1 ps-6">
              {ineligible.map((reason) => (
                <li key={reason.code}>{reasonText(reason, locale, m)}</li>
              ))}
            </ul>
          ) : null}
          <p>{owner ? copy.ownerMay : copy.ownerOnly}</p>
        </Notice>
      ) : null}
      {ineligible && owner ? (
        <Field label={copy.overrideReason} hint={copy.overrideReasonHint} required>
          {(control) => (
            <StaffTextarea
              {...control}
              rows={2}
              maxLength={500}
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
            />
          )}
        </Field>
      ) : null}

      <ProblemNotice problem={problem} />
      <div className="flex flex-wrap gap-3">
        <Button
          variant="staff"
          busy={busy}
          disabled={!ready && !busy}
          aria-describedby={!ready && !busy && reasons.length > 0 ? reasonsId : undefined}
          onClick={() => void save()}
        >
          {current ? copy.saveReplace : copy.save}
        </Button>
        <Button variant="outline" className="rounded-sm" onClick={() => onDone(null)}>
          {copy.cancel}
        </Button>
      </div>
      <ReasonList id={reasonsId} reasons={busy ? [] : reasons} />
    </div>
  )
}
